import React from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'

import CopyButton from '../components/CopyButton'
import I18n, { Trans } from '../i18n'
import Section from '../components/Section'
import SubscriptionSummary from '../components/SubscriptionSummary'
import Headline from '../components/Headline'
import Button from '../components/Button'
import Card from '../components/Card'
import ProfileForm from '../forms/ProfileForm'

function Profile({
  user,
  publicApiToken,
  subscribedToNewsletter,
  subscription,
  originalUrl
}) {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
  
  const handleFormSubmit = async (e, successRedirect) => {
    e.preventDefault()
    const form = e.target
    const formData = new FormData(form)
    
    try {
      const response = await fetch(form.action, {
        method: form.method,
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        // Use redirect from server response or fallback
        window.location.href = data.redirect_to || successRedirect || '/'
      } else {
        console.error('Form submission failed:', response.statusText)
      }
    } catch (error) {
      console.error('Form submission error:', error)
    }
  }
  
  return (
    <Section mid css={{ minHeight: '100%', padding: '3rem 0' }}>
      <ProfileWrapper>
        <Helmet>
          <title>{I18n.t('profile.title')}</title>
        </Helmet>
        <Card>
          <Headline>
            <Trans id="profile.headline" />
          </Headline>
          <ProfileForm
            values={user.values}
            errors={user.errors}
            hasSubscription={!!subscription}
          />
        </Card>
        <CardRow>
          <Card>
            {publicApiToken ? (
              <>
                <Headline css={{ marginBottom: '1rem' }}>
                  <Trans id="profile.api_token.present.headline" />
                </Headline>
                <CopyButton value={publicApiToken} />
                <Trans id="profile.api_token.present.info" copy />
                <ButtonsContainer>
                  <Button.Link
                    href="mailto:info@hystreet.com"
                    css={{ marginTop: '1rem' }}
                  >
                    <Trans id="profile.api_token.present.button" />
                  </Button.Link>
                  <Button.Link
                    outline
                    href="/developer"
                    css={{ marginTop: '1rem' }}
                  >
                    <Trans id="profile.api_token.developer_page" />
                  </Button.Link>
                </ButtonsContainer>
              </>
            ) : (
              <>
                <Headline css={{ marginBottom: '1rem' }}>
                  <Trans id="profile.api_token.generate.headline" />
                </Headline>
                <Trans id="profile.api_token.info" />
                <ButtonsContainer>
                  <form 
                    method="post" 
                    action="/create_api_token" 
                    style={{ display: 'inline' }}
                    onSubmit={(e) => handleFormSubmit(e, '/profile')}
                  >
                    <input type="hidden" name="_method" value="put" />
                    <input type="hidden" name="authenticity_token" value={csrfToken} />
                    <Button
                      type="submit"
                      css={{ marginTop: '1rem' }}
                    >
                      <Trans id="profile.api_token.button" />
                    </Button>
                  </form>
                  <Button.Link
                    outline
                    href="/developer"
                    css={{ marginTop: '1rem' }}
                  >
                    <Trans id="profile.api_token.developer_page" />
                  </Button.Link>
                </ButtonsContainer>
              </>
            )}
          </Card>
          <Card>
            <Headline css={{ marginBottom: '1rem' }}>
              <Trans id="profile.newsletter.headline" />
            </Headline>
            {subscribedToNewsletter ? (
              <>
                <Trans id="profile.newsletter.subscribed" copy />
                <form 
                  method="post" 
                  action="/optout" 
                  style={{ display: 'inline' }}
                  onSubmit={(e) => {
                    if (window.confirm(I18n.t('profile.newsletter.unsubscribe_confirmation_text'))) {
                      handleFormSubmit(e, '/profile')
                    } else {
                      e.preventDefault()
                    }
                  }}
                >
                  <input type="hidden" name="_method" value="delete" />
                  <input type="hidden" name="authenticity_token" value={csrfToken} />
                  <Button
                    type="submit"
                    outline
                    css={{ marginTop: '2rem' }}
                  >
                    <Trans id="profile.newsletter.unsubscribe" />
                  </Button>
                </form>
              </>
            ) : (
              <>
                <Trans id="profile.newsletter.not_subscribed" copy />
                <form 
                  method="post" 
                  action="/optin" 
                  style={{ display: 'inline' }}
                  onSubmit={(e) => handleFormSubmit(e, '/profile')}
                >
                  <input type="hidden" name="authenticity_token" value={csrfToken} />
                  <Button
                    type="submit"
                    css={{ marginTop: '2rem', alignSelf: 'flex-start' }}
                  >
                    <Trans id="profile.newsletter.subscribe" />
                  </Button>
                </form>
              </>
            )}
          </Card>
        </CardRow>
        <CardRow>
          <Card style={{ width: '100%' }}>
            <SubscriptionSummary subscription={subscription} />
          </Card>
        </CardRow>
        <CardRow>
          <Card>
            <Headline css={{ marginBottom: '2rem' }}>
              <Trans id="profile.end_account.headline" />
            </Headline>
            {subscription ? (
              <>
                <Trans id="profile.end_account.paid_plan_subline" copy />
                <Button.Link
                  outline
                  href="/contact"
                  css={{ marginTop: '2rem', alignSelf: 'flex-start' }}
                >
                  <Trans id="profile.end_account.paid_plan_button_text" />
                </Button.Link>
              </>
            ) : (
              <>
                <Trans id="profile.end_account.free_plan_subline" copy />
                <form 
                  method="post" 
                  action="/signup" 
                  style={{ display: 'inline' }}
                  onSubmit={(e) => {
                    if (window.confirm(I18n.t('profile.end_account.confirmation_text'))) {
                      handleFormSubmit(e, '/')
                    } else {
                      e.preventDefault()
                    }
                  }}
                >
                  <input type="hidden" name="_method" value="delete" />
                  <input type="hidden" name="authenticity_token" value={csrfToken} />
                  <Button
                    type="submit"
                    outline
                    css={{ marginTop: '2rem', alignSelf: 'flex-start' }}
                  >
                    <Trans id="profile.end_account.free_plan_button_text" />
                  </Button>
                </form>
              </>
            )}
          </Card>
          <Card>
            <Headline css={{ marginBottom: '2rem' }}>
              <Trans id="profile.logout.headline" />
            </Headline>
              <>
                <form 
                  method="post" 
                  action="/logout" 
                  style={{ display: 'inline' }}
                  onSubmit={(e) => {
                    if (window.confirm(I18n.t('profile.logout.confirmation_text'))) {
                      handleFormSubmit(e, '/')
                    } else {
                      e.preventDefault()
                    }
                  }}
                >
                  <input type="hidden" name="_method" value="delete" />
                  <input type="hidden" name="authenticity_token" value={csrfToken} />
                  <Button
                    type="submit"
                    outline
                    css={{ alignSelf: 'flex-start' }}
                  >
                    <Trans id="profile.logout.button_text" />
                  </Button>
                </form>
              </>
          </Card>
        </CardRow>
      </ProfileWrapper>
    </Section>
  )
}

const ProfileWrapper = styled(Section.Wrapper)`
  ${p => p.theme.breakpoints.phone} {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
`

const CardRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;

  > div {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-top: 1rem;
    width: calc(50% - 0.5rem);

    ${p => p.theme.breakpoints.tablet} {
      width: 100%;
    }
  }
`

const ButtonsContainer = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (min-width: 1170px) {
    flex-direction: row;
    a {
      width: calc(50% - 0.5rem);
    }
  }
`

Profile.propTypes = {
  user: PropTypes.shape({
    values: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired
  }).isRequired,
  publicApiToken: PropTypes.string,
  subscribedToNewsletter: PropTypes.bool,
  subscription: PropTypes.object
}

export default Profile
